import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useApiQuery } from '../use-api.js';
import { ReviewersPanel } from '../components/ReviewersPanel.js';
import { PrStatusCard } from '../components/PrStatusCard.js';
import { CouncilRunsTable } from '../components/CouncilRunsTable.js';
import { SlicingPanel } from '../components/SlicingPanel.js';
import { LinkedIncidents, type LinkedIncident } from '../components/LinkedIncidents.js';
import { ConceptHelpIcon } from '../components/ConceptHelpIcon.js';

interface PrRecord {
  meta?: Record<string, unknown> | null;
  artifacts?: Record<string, unknown>[];
  slash_reruns?: Record<string, unknown>[];
  reviews?: Record<string, unknown>[];
  // Legacy GSI-shape rows from the old ingest-confluence-postmortem handler.
  // No longer rendered — the new heuristic linker's linked_incidents
  // subsumes the data shown to users. Kept on the type so a stale response
  // doesn't crash the parser.
  incidents?: Record<string, unknown>[];
  linked_incidents?: LinkedIncident[];
  [key: string]: unknown;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card">
      <h3 style={{ marginBottom: 12, fontSize: '0.875rem', fontWeight: 664, color: 'var(--loop-neutral-900)' }}>{title}</h3>
      {children}
    </div>
  );
}

function ArraySection({ items }: { items: Record<string, unknown>[] }) {
  if (!items || items.length === 0) return <p style={{ color: 'var(--loop-neutral-500)', fontSize: '0.875rem' }}>None.</p>;
  const keys = [...new Set(items.flatMap(i => Object.keys(i)))];
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ minWidth: '100%', fontSize: '0.875rem', borderCollapse: 'collapse' }}>
        <thead style={{ textAlign: 'left', background: 'var(--loop-neutral-200)' }}>
          <tr>
            {keys.map(k => (
              <th key={k} style={{ padding: '6px 12px', fontSize: '0.75rem', color: 'var(--loop-neutral-700)', fontWeight: 528 }}>{k}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => (
            <tr key={i} style={{ borderTop: '1px solid var(--loop-neutral-200)' }}>
              {keys.map(k => (
                <td key={k} style={{ padding: '6px 12px', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                  {item[k] === undefined ? '' : typeof item[k] === 'object' ? JSON.stringify(item[k]) : String(item[k])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function PrDetail() {
  const { org, repo, pr } = useParams<{ org: string; repo: string; pr: string }>();
  const [includeLowConfidence, setIncludeLowConfidence] = useState(false);
  // The toggle re-issues the API call with ?include_low_confidence=true so
  // arch_group_only / timing_only chips are revealed without a separate
  // client-side filter. Keep the React-Query cache keyed on the flag.
  const path = `/api/pr/${org}/${repo}/${pr}${includeLowConfidence ? '?include_low_confidence=true' : ''}`;
  const { data, isLoading, error } = useApiQuery<PrRecord>(
    ['pr', org, repo, pr, includeLowConfidence],
    path
  );

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Link to={`/repos/${org}/${repo}`} style={{ color: 'var(--loop-blue-600)', fontSize: '0.875rem' }}>
          ← {org}/{repo}
        </Link>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--loop-space-100)', marginBottom: 16 }}>
        <h2 style={{ fontSize: '1.125rem', fontWeight: 664, margin: 0, color: 'var(--loop-neutral-1100)' }}>
          PR #{pr} — {org}/{repo}
        </h2>
        <ConceptHelpIcon slug="verdict" />
      </div>

      {isLoading && <p style={{ color: 'var(--loop-neutral-700)', fontSize: '0.875rem' }}>Loading…</p>}
      {error && <p className="error">Error: {String(error)}</p>}

      {data && (
        <>
          <Section title="PR Status">
            <PrStatusCard
              meta={data.meta ?? null}
              artifacts={(data.artifacts ?? []) as Record<string, unknown>[]}
              org={org}
              repo={repo}
              pr={pr}
            />
          </Section>
          <Section title="Slicing">
            <SlicingPanel artifacts={(data.artifacts ?? []) as Record<string, unknown>[]} />
          </Section>
          <Section title="Reviewers">
            <ReviewersPanel
              artifacts={(data.artifacts ?? []) as Record<string, unknown>[]}
              slashReruns={(data.slash_reruns ?? []) as Record<string, unknown>[]}
            />
          </Section>
          <Section title="Council runs">
            <CouncilRunsTable artifacts={(data.artifacts ?? []) as Record<string, unknown>[]} />
          </Section>
          <Section title="Reviews">
            <ArraySection items={(data.reviews ?? []) as Record<string, unknown>[]} />
          </Section>
          <Section title="Linked incidents">
            <LinkedIncidents
              incidents={data.linked_incidents ?? []}
              includeLowConfidence={includeLowConfidence}
              onToggleLowConfidence={() => setIncludeLowConfidence((v) => !v)}
            />
          </Section>
        </>
      )}
    </div>
  );
}
